import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import API_BASE from "../../config";
import { extractErrorMessage } from "../../utils/apiError";
import ConfirmDialog from "../../components/ConfirmDialog";
import s from "./RoomsPanel.module.css";

const ROOM_TYPES = [
  { value: "STANDARD",   label: "Standard" },
  { value: "LAB",        label: "Lab" },
  { value: "MUSIC",      label: "Music" },
  { value: "GYM",        label: "Gym" },
  { value: "LIBRARY",    label: "Library" },
  { value: "AUDITORIUM", label: "Auditorium" },
];

const BLANK_ROOM = {
  id: null, name: "", building: "", floor: "", capacity: "", roomType: "STANDARD",
};

function authHeaders() {
  let user = null;
  try { user = JSON.parse(sessionStorage.getItem("user") || localStorage.getItem("user") || "null"); }
  catch { /* ignore */ }
  return {
    "Content-Type": "application/json",
    "X-User-Role":  user?.role     || "",
    "X-Username":   user?.username || "",
  };
}

function pillClassFor(roomType) {
  switch ((roomType || "").toUpperCase()) {
    case "LAB":     return s.typePillLab;
    case "MUSIC":   return s.typePillMusic;
    case "GYM":     return s.typePillGym;
    case "LIBRARY": return s.typePillLibrary;
    default:        return null;
  }
}

/**
 * CRUD admin panel for the Rooms catalog. Premium card grid with filters,
 * inline edit modal, and a styled delete-confirm that surfaces the
 * server's "in use by N section(s) and M offering(s)" message verbatim.
 */
export default function RoomsPanel() {
  const navigate = useNavigate();
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [filterBuilding, setFilterBuilding] = useState("");
  const [filterType, setFilterType] = useState("");

  // Edit/create modal state
  const [editing, setEditing] = useState(null); // null | "new" | row
  const [form, setForm] = useState(BLANK_ROOM);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState("");

  // Delete confirm state
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  async function loadRooms() {
    setLoading(true); setError("");
    try {
      const res = await fetch(`${API_BASE}/api/rooms`);
      if (!res.ok) throw new Error(await extractErrorMessage(res, `Request failed (${res.status})`));
      const list = await res.json();
      setRooms(Array.isArray(list) ? list : []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { loadRooms(); }, []);

  // Escape closes the edit/new modal (outside-click no longer does);
  // cancelEdit() already no-ops while busy.
  useEffect(() => {
    if (!editing) return;
    function onKey(e) { if (e.key === "Escape") cancelEdit(); }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, busy]);

  const buildings = useMemo(() => {
    const set = new Set();
    for (const r of rooms) if (r.building) set.add(r.building);
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [rooms]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rooms.filter(r => {
      if (filterBuilding && (r.building || "") !== filterBuilding) return false;
      if (filterType && (r.roomType || "") !== filterType) return false;
      if (!q) return true;
      const hay = [r.name, r.building, r.floor, r.roomType]
        .filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [rooms, search, filterBuilding, filterType]);

  function openNew() {
    setEditing("new");
    setForm({ ...BLANK_ROOM });
    setFormError("");
  }
  function openEdit(row) {
    setEditing(row);
    setForm({
      id: row.id,
      name: row.name || "",
      building: row.building || "",
      floor: row.floor || "",
      capacity: row.capacity == null ? "" : String(row.capacity),
      roomType: row.roomType || "STANDARD",
    });
    setFormError("");
  }
  function cancelEdit() {
    if (busy) return;
    setEditing(null);
    setForm(BLANK_ROOM);
    setFormError("");
  }

  async function save() {
    const name = (form.name || "").trim();
    if (!name) { setFormError("Name is required."); return; }
    if (form.capacity !== "" && Number.isNaN(Number(form.capacity))) {
      setFormError("Capacity must be a number."); return;
    }
    setBusy(true); setFormError("");
    try {
      const payload = {
        name,
        building: (form.building || "").trim() || null,
        floor:    (form.floor    || "").trim() || null,
        capacity: form.capacity === "" ? null : Number(form.capacity),
        roomType: form.roomType || null,
      };
      const isNew = editing === "new";
      const url   = isNew ? `${API_BASE}/api/rooms` : `${API_BASE}/api/rooms/${editing.id}`;
      const res = await fetch(url, {
        method: isNew ? "POST" : "PUT",
        headers: authHeaders(),
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(await extractErrorMessage(res, `Request failed (${res.status})`));
      await loadRooms();
      setEditing(null);
      setForm(BLANK_ROOM);
    } catch (e) {
      setFormError(e.message);
    } finally {
      setBusy(false);
    }
  }

  function askDelete(row) { setPendingDelete(row); }
  function cancelDelete() { if (deleteBusy) return; setPendingDelete(null); }
  async function confirmDelete() {
    if (!pendingDelete) return;
    setDeleteBusy(true);
    try {
      const res = await fetch(`${API_BASE}/api/rooms/${pendingDelete.id}`, {
        method: "DELETE",
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error(await extractErrorMessage(res, `Request failed (${res.status})`));
      await loadRooms();
    } catch (e) {
      // Show the server's "in-use" message via the page-level error
      // banner since the dialog is closing. Future polish: surface
      // inside the dialog instead.
      setError(e.message);
    } finally {
      setDeleteBusy(false);
      setPendingDelete(null);
    }
  }

  return (
    <>
      <div className={s.filterBar}>
        <label className={s.filterField}>
          <span>Building</span>
          <select value={filterBuilding} onChange={e => setFilterBuilding(e.target.value)}>
            <option value="">All buildings</option>
            {buildings.map(b => <option key={b} value={b}>{b}</option>)}
          </select>
        </label>
        <label className={s.filterField}>
          <span>Type</span>
          <select value={filterType} onChange={e => setFilterType(e.target.value)}>
            <option value="">All types</option>
            {ROOM_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </label>
        <label className={s.filterField}>
          <span>Search</span>
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Name, building, floor…"
          />
        </label>
        <button type="button" className={s.btnPrimary} onClick={openNew}>
          + New room
        </button>
      </div>

      <div className={s.summary}>
        <span className={s.pill}>
          <span className={s.pillCount}>{visible.length}</span> of {rooms.length} room{rooms.length === 1 ? "" : "s"}
        </span>
      </div>

      {error && <div className={s.error}>{error}</div>}

      {loading ? (
        <div className={s.empty}>Loading rooms…</div>
      ) : rooms.length === 0 ? (
        <div className={s.empty}>
          <div className={s.emptyTitle}>No rooms yet</div>
          <div>Click "+ New room" to add your first one.</div>
        </div>
      ) : visible.length === 0 ? (
        <div className={s.empty}>
          <div className={s.emptyTitle}>No matches</div>
          <div>Try clearing filters or adjusting your search.</div>
        </div>
      ) : (
        <div className={s.grid}>
          {visible.map(r => {
            const pill = pillClassFor(r.roomType);
            return (
              <div
                key={r.id}
                className={`${s.card} ${s.cardClickable}`}
                role="button"
                tabIndex={0}
                aria-label={`Open ${r.name}`}
                onClick={() => navigate(`/academics/rooms/${r.id}`)}
                onKeyDown={(e) => {
                  // Mirror SectionsByGradePanel: only handle keys
                  // originating on the card itself, not its inner buttons.
                  if (e.target !== e.currentTarget) return;
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    navigate(`/academics/rooms/${r.id}`);
                  }
                }}>
                <div className={s.cardHead}>
                  <div className={s.cardName}>{r.name}</div>
                  {r.roomType && (
                    <span className={`${s.typePill} ${pill || ""}`}>{r.roomType}</span>
                  )}
                </div>
                {(r.building || r.floor) && (
                  <div className={s.meta}>
                    {r.building && <span>{r.building}</span>}
                    {r.building && r.floor && <span className={s.metaSep}>·</span>}
                    {r.floor && <span>Floor {r.floor}</span>}
                  </div>
                )}
                <div className={s.statRow}>
                  {r.capacity != null && (
                    <span className={`${s.statPill} ${s.statPillCapacity}`}>
                      {r.capacity} seats
                    </span>
                  )}
                </div>
                <div className={s.cardActions}>
                  <button
                    type="button"
                    className={s.btnEdit}
                    onClick={(e) => { e.stopPropagation(); openEdit(r); }}>
                    Edit
                  </button>
                  <button
                    type="button"
                    className={s.btnDelete}
                    onClick={(e) => { e.stopPropagation(); askDelete(r); }}>
                    Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editing && createPortal(
        <div className={s.backdrop}>
          <div className={s.modal} role="dialog" aria-modal="true" aria-labelledby="rp-modal-title">
            <div className={s.modalHead}>
              <h3 id="rp-modal-title" className={s.modalTitle}>
                {editing === "new" ? "New room" : "Edit room"}
              </h3>
              <p className={s.modalSub}>
                Rooms are referenced by sections (homeroom) and class offerings.
              </p>
            </div>
            <div className={s.modalBody}>
              {formError && <div className={s.error}>{formError}</div>}
              <div className={s.formGrid}>
                <div className={`${s.formField} ${s.formGridFull}`}>
                  <label className={s.formLabel}>
                    Name<span className={s.formReq}> *</span>
                  </label>
                  <input
                    className={s.formInput}
                    type="text"
                    value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="Rm 201"
                    autoFocus
                    disabled={busy}
                  />
                </div>
                <div className={s.formField}>
                  <label className={s.formLabel}>Building</label>
                  <input
                    className={s.formInput}
                    type="text"
                    value={form.building}
                    onChange={e => setForm(f => ({ ...f, building: e.target.value }))}
                    placeholder="Main"
                    disabled={busy}
                  />
                </div>
                <div className={s.formField}>
                  <label className={s.formLabel}>Floor</label>
                  <input
                    className={s.formInput}
                    type="text"
                    value={form.floor}
                    onChange={e => setForm(f => ({ ...f, floor: e.target.value }))}
                    placeholder="2"
                    disabled={busy}
                  />
                </div>
                <div className={s.formField}>
                  <label className={s.formLabel}>Capacity</label>
                  <input
                    className={s.formInput}
                    type="number"
                    min="0"
                    value={form.capacity}
                    onChange={e => setForm(f => ({ ...f, capacity: e.target.value }))}
                    placeholder="40"
                    disabled={busy}
                  />
                </div>
                <div className={s.formField}>
                  <label className={s.formLabel}>Type</label>
                  <select
                    className={s.formSelect}
                    value={form.roomType}
                    onChange={e => setForm(f => ({ ...f, roomType: e.target.value }))}
                    disabled={busy}>
                    {ROOM_TYPES.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            <div className={s.modalFoot}>
              <button type="button" className={s.btnGhost} onClick={cancelEdit} disabled={busy}>
                Cancel
              </button>
              <button type="button" className={s.btnPrimary} onClick={save} disabled={busy}>
                {busy ? "Saving…" : (editing === "new" ? "Create room" : "Save changes")}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      <ConfirmDialog
        open={!!pendingDelete}
        tone="danger"
        title="Delete room?"
        message="This action cannot be undone."
        description={pendingDelete?.name || null}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        busy={deleteBusy}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />
    </>
  );
}
